import { useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import updateChannelModuleMutation from 'lane-shared/graphql/modules/updateChannelModule';
import type { WorkOrderModuleConfiguration } from 'lane-shared/domains/workOrder/types/workOrder';
import { ModuleCategoryEnum } from 'lane-shared/types/modules/modulesEnums';
import type { Channel } from 'lane-shared/types/ChannelType';
import { useWorkOrderValidationSchema } from './useWorkOrderValidationSchema';
import { ValidationError } from 'yup';

export const useWorkOrderConfigurationForm = (
  channel?: Pick<Channel, '_id' | 'channelModules'>,
  refetchChannel = () => {},
) => {
  const { t } = useTranslation();
  const [updateChannelModule] = useMutation(updateChannelModuleMutation);
  const schema = useWorkOrderValidationSchema();

  const workOrdersModule = channel?.channelModules?.find(
    module => module.name === ModuleCategoryEnum.WorkOrders
  );
  const moduleConfiguration = workOrdersModule?.settings?.configuration;

  const initialConfiguration: WorkOrderModuleConfiguration = {
    messageNotification: {
      tenantChat: moduleConfiguration?.messageNotification?.tenantChat ?? false,
    },
    workOrderSurveys: {
      allowFeedback: moduleConfiguration?.workOrderSurveys?.allowFeedback ?? false,
      autoSurveyTriggerStatus:
        moduleConfiguration?.workOrderSurveys?.autoSurveyTriggerStatus ?? null,
    },
  };

  const form = useForm<WorkOrderModuleConfiguration>({
    defaultValues: initialConfiguration,
    mode: 'onChange',
    shouldUnregister: false,
    resolver: async (data) => {
      try {
        await schema.validate(data, { abortEarly: false });

        return { values: data, errors: {} };
      } catch (err: unknown) {
        const errors: Record<string, { message: string }> = {};

        if (err instanceof ValidationError && err.inner?.length > 0) {
          err.inner.forEach((e) => {
            if (e.path) {
              errors[e.path] = { message: e.message };
            }
          });
        }

        return { values: data, errors };
      }

    },
  });

  const {
    reset,
    handleSubmit,
    formState: { isDirty, isValid },
  } = form;

  const saveConfiguration = handleSubmit(async (data) => {
    try {
      await updateChannelModule({
        variables: {
          channelModule: {
            ...workOrdersModule,
            __typename: undefined,
            channelId: channel?._id,
            settings: {
              ...workOrdersModule?.settings,
                configuration: data,
            },
          },
        },
      });

      await refetchChannel();
      reset(data);

      window.Toast.show(
        t('web.admin.serviceRequest.settings.toast.successMessage')
      );
    } catch (ex) {
      console.error(ex);
    }
  });

  const undoConfiguration = () => reset(initialConfiguration);

  return { form, isDirty, isValid, saveConfiguration, undoConfiguration };
};
